import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Tabs,
  Tab,
  Collapse,
} from "@mui/material";
import { Globe, FileText, Pencil, Trash2, Upload, Plus, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { endpoint } from "../../resource/Constant";
import { useSnackbar } from "notistack";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

interface PdfFile {
  id: string;
  name: string;
  sizeMb?: number | string;
  uploadedAt?: Date | string | null;
}

interface ChatTranscript {
  role: "User" | "Assistant";
  message: string;
  timestamp: string;
}

interface UserChatData {
  user_name: string;
  user_email: string;
  user_mobile_number: string;
  chat_transcript: ChatTranscript[];
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(searchParams.get("tab") === "transcripts" ? 1 : 0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });
  
  // Mock data - replace with your API calls
  const [websiteUrl, setWebsiteUrl] = useState("www");
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editedUrl, setEditedUrl] = useState(websiteUrl);
  const [pdfFiles, setPdfFiles] = useState<PdfFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatTranscripts, setChatTranscripts] = useState<UserChatData[]>([]);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());

  const { enqueueSnackbar } = useSnackbar();
    
  const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
      enqueueSnackbar(message, { variant: severity });
  };

  useEffect(() => {
    const handleGetUrl = async () => {
      try {
        const response = await axios.get(`${endpoint}/api/crawling/website-link`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("crawling")}`,
          },
        });
        const website_link = response?.data?.data?.website_link || "";
        setWebsiteUrl(website_link);
      } catch (error) {
        const status = error?.response?.status
        if (status !== 401 && status !== 403) {
          console.log('status' , status)
          showSnackbar(
            error?.response?.data?.detail ||
              error?.response?.data?.message ||
              "Internal Server Error",
            "error"
          );
        }
        
      }
    };

    handleGetUrl();
  }, []);

  useEffect(() => {
    if (tabValue === 1) {
      fetchChatTranscripts();
    }
  }, [tabValue]);

  const fetchChatTranscripts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${endpoint}/api/crawling/get-chat-transcript`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("crawling")}`,
        },
      });
      setChatTranscripts(response?.data?.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const status = error?.response?.status;
      if (status === 404) {
        setChatTranscripts([]);
        return;
      }
      showSnackbar(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to fetch transcripts",
        "error"
      );
      if (status === 401 || status === 403) {
        localStorage.removeItem('crawling');
        navigate('/crawling/signin');
      }
    }
  };

  useEffect(() => {
    const handleGetFiles = () => {
    axios.get(`${endpoint}/api/crawling/list-files` , {
      headers : {
        Authorization : `Bearer ${localStorage.getItem("crawling")}`,
      },
    })
    .then((response) => {
      const rawFiles = response?.data?.data?.files || [];
      const normalizedFiles: PdfFile[] = rawFiles.map((file: any, idx: number) => {
        return {
          id: file?.id ?? `${idx}-${file?.name ?? "file"}`,
          name: file?.name ?? "Untitled",
          sizeMb: file?.size_mb ?? "",
          uploadedAt: file?.updated_at ?? null,
        };
      });
      setPdfFiles(normalizedFiles);
    })
    .catch((error) => {
      const status = error?.response?.status;
      if (status === 404) {
        return;
      }
      showSnackbar(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Internal Server Error",
          "error"
      );
      if (status === 401 || status === 403) {
        localStorage.removeItem('crawling');
        navigate('/crawling/signin')
      }
    })
  }
      handleGetFiles();
  } , [])

  const handleSaveUrl = async () => {
    // TODO: Call your API to update website URL
    setIsEditingUrl(false);
    setWebsiteUrl(editedUrl);
    setLoading(true)
    try {
      await axios.post(`${endpoint}/api/crawling/website-crawl`,
        {website_link : editedUrl},
        {headers : {
          Authorization : `Bearer ${localStorage.getItem('crawling')}`
        }}
      )
      showSnackbar("Website URL has been updated, click create knowledge base for new data", "success");
      setLoading(false)
    } catch (error) {
      setLoading(false)
      showSnackbar(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Internal Server Error",
        "error"
      );
      const status = error?.response?.status;

    }
  };

  const handleDeleteUrl = () => {
    // TODO: Call your API to delete website URL
    setWebsiteUrl("");
    setSnackbar({ open: true, message: "Website URL has been removed.", severity: "success" });
  };

  const handleUploadPdf = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setSelectedFile(file);
      }
    };
    input.click();
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    try {
      // TODO: Call your API to upload PDF
      const formData = new FormData();
      formData.append("file", selectedFile);

      const fileName = selectedFile.name;
      const newPdf: PdfFile = {
        id: Date.now().toString(),
        name: fileName,
        sizeMb: Number((selectedFile.size / 1024 / 1024).toFixed(1)),
        uploadedAt: new Date(),
      };
      setPdfFiles([...pdfFiles, newPdf]);
      setSelectedFile(null);

      axios.post(`${endpoint}/api/crawling/add-file` , formData, {headers : {Authorization : `Bearer ${localStorage.getItem('crawling')}` , "Content-Type": "multipart/form-data",}})
      .then((res) => {
        showSnackbar(`${fileName} has been uploaded successfully.`, "success");
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.detail || error?.response?.data?.message || "Internal Server Error", "error");
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('crawling')
          navigate('/crawling/signin')
        }
      })



    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to upload file",
        "error"
      );
    }
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
  };

  const handleDeletePdf = (id: string, name: string) => {
    // TODO: Call your API to delete PDF
    setPdfFiles(pdfFiles.filter(pdf => pdf.id !== id));

    axios.delete(`${endpoint}/api/crawling/delete-file` , {
      params : {
        file_name_object : name
      },
      headers : {
        Authorization : `Bearer ${localStorage.getItem('crawling')}`
      }
    })
    .then((res) => {
      setSnackbar({ open: true, message: `${name} has been removed.`, severity: "success" });
    })
    .catch((error) => {
      setSnackbar({ open: true, message: error?.response?.data?.detail || error?.response?.data?.message || 'Internal Server Error' , severity: "error" });
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('crawling')
        navigate('/crawling/signin')
      }
    })
  };

  const handleCreateKnowledgeBase = () => {
    setLoading(true)
    axios.post(`${endpoint}/api/crawling/vector-database` , {} , {headers : {Authorization : `Bearer ${localStorage.getItem('crawling')}`}})
    .then((res) => {
      setSnackbar({ open: true, message: "Vectore Database created successfully" , severity: "success" });
      setLoading(false)
    })
    .catch((error) => {
      setLoading(false)
      setSnackbar({ open: true, message: error?.response?.data?.detail || error?.response?.data?.message || 'Internal Server Error' , severity: "error" });
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('crawling')
        navigate('/crawling/signin')
      }
    })
  }

  const toggleExpandEmail = (email: string) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(email)) {
      newExpanded.delete(email);
    } else {
      newExpanded.add(email);
    }
    setExpandedEmails(newExpanded);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 4 }}>
      <Box sx={{ maxWidth: 1152, mx: "auto" }}>
        {/* Tabs Navigation */}
        <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your website, documents, and view chat transcripts
            </Typography>
          </Box>
          <Button
            variant="contained"
            style={{ backgroundColor: "green" }}
            disabled={loading || tabValue === 1}
            onClick={handleCreateKnowledgeBase}
          >
            Create Knowledge Base
          </Button>
          <Button variant="outlined" onClick={() => navigate("/crawling/index")}>
            Back to Home
          </Button>
        </Box>

        {/* Tab Buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant={tabValue === 0 ? "contained" : "outlined"}
            onClick={() => setTabValue(0)}
            startIcon={<FileText size={18} />}
          >
            Content & Settings
          </Button>
          <Button
            variant={tabValue === 1 ? "contained" : "outlined"}
            onClick={() => setTabValue(1)}
            startIcon={<MessageCircle size={18} />}
          >
            Chat Transcripts
          </Button>
        </Stack>

        {/* Content & Settings Tab */}
        {tabValue === 0 && (
          <Stack spacing={3}>
            {/* Website URL Section */}
            <Card>
              <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Globe size={20} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Crawled Website
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  The website URL used for crawling content
                </Typography>
              </Box>
              <CardContent>
                {websiteUrl ? (
                  <Stack spacing={2}>
                    {isEditingUrl ? (
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        value={editedUrl}
                        onChange={(e) => setEditedUrl(e.target.value)}
                        placeholder="Enter website URL"
                      />
                      <Button variant="contained" onClick={handleSaveUrl}>
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditingUrl(false);
                          setEditedUrl(websiteUrl);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Globe size={20} color="#1976d2" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {websiteUrl}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setIsEditingUrl(true);
                            setEditedUrl(websiteUrl);
                          }}
                        >
                          <Pencil size={16} />
                        </IconButton>
                        {/* <IconButton size="small" color="error" onClick={handleDeleteUrl}>
                          <Trash2 size={16} />
                        </IconButton> */}
                      </Stack>
                    </Paper>
                  )}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Globe size={48} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      No website URL configured
                    </Typography>
                    <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => navigate("/crawling/index")}>
                      Add Website
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* PDF Files Section */}
            <Card>
              <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <FileText size={20} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      PDF Documents
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded PDF files for the knowledge base
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Upload size={16} />} onClick={handleUploadPdf}>
                  Upload New
                </Button>
              </Box>
            </Box>
            <CardContent>
              {/* Selected File Preview Box */}
              {selectedFile && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: "action.selected",
                    border: "2px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "primary.main" }}>
                        Selected File
                      </Typography>
                      <IconButton size="small" onClick={handleRemoveSelectedFile}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FileText size={20} color="#1976d2" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number((selectedFile.size / 1024 / 1024).toFixed(1))} MB
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" onClick={handleRemoveSelectedFile}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleConfirmUpload}>
                        Upload
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
              {pdfFiles.length > 0 ? (
                <Stack spacing={1.5}>
                  {pdfFiles.map((pdf) => (
                    <Paper
                      key={pdf.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <FileText size={20} color="#1976d2" />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {pdf.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(pdf.sizeMb ?? "Unknown size") + (pdf.sizeMb ? " MB" : "")} • Uploaded{" "}
                            {pdf.uploadedAt
                              ? new Date(pdf.uploadedAt).toLocaleString()
                              : "Unknown date"}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletePdf(pdf.id, pdf.name)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <FileText size={48} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No PDF files uploaded
                  </Typography>
                  {/* <Button
                    variant="contained"
                    startIcon={<Upload size={16} />}
                    onClick={handleUploadPdf}
                  >
                    Upload PDF
                  </Button> */}
                </Box>
              )}
            </CardContent>
            </Card>
          </Stack>
        )}

        {/* Chat Transcripts Tab */}
        {tabValue === 1 && (
          <Stack spacing={3}>
            <Card>
              <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <MessageCircle size={20} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Chat Transcripts
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  View conversations between users and your AI chatbot
                </Typography>
              </Box>
              <CardContent>
                {chatTranscripts.length > 0 ? (
                  <Stack spacing={2}>
                    {chatTranscripts.map((userChat, idx) => (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 0,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {/* User Info Header */}
                        <Box
                          sx={{
                            p: 2.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: "linear-gradient(90deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.04))",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: "linear-gradient(90deg, rgba(25, 118, 210, 0.15), rgba(25, 118, 210, 0.1))",
                            },
                            transition: "background-color 0.2s",
                          }}
                          onClick={() => toggleExpandEmail(userChat.user_email)}
                        >
                          <Box sx={{ flex: 1}}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                              {userChat.user_name}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                📧 {userChat.user_email ? userChat.user_email : "No email provided"}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                📱 {userChat.user_mobile_number ? userChat.user_mobile_number : "No mobile number provided"}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {userChat.chat_transcript.length} messages
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            {expandedEmails.has(userChat.user_email) ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </IconButton>
                        </Box>

                        {/* Messages */}
                        <Collapse in={expandedEmails.has(userChat.user_email)}>
                          <Box sx={{ p: 2.5, bgcolor: "rgba(0, 0, 0, 0.02)", overflowY: "auto" , maxHeight: 400 }}>
                            <Stack spacing={2}>
                              {userChat.chat_transcript.length > 0 ? (
                                userChat.chat_transcript.map((msg, msgIdx) => (
                                  <Box
                                    key={msgIdx}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: msg.role === "User" ? "flex-end" : "flex-start",
                                    }}
                                  >
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        maxWidth: "75%",
                                        bgcolor:
                                          msg.role === "User"
                                            ? "white"
                                            : "#1976d2",
                                        color:
                                          msg.role === "User"
                                            ? "black"
                                            : "#ffffff",
                                        borderRadius: 2,
                                        borderColor: msg.role === "User" ? "black" : "black",
                                      }}
                                    >
                                      <Typography variant="body2" sx={{color: msg.role === "User" ? "black" : "white", mb: 0.5, fontWeight: 500 }}>
                                        {msg.role}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: msg.role === "User" ? "black" : "#ffffff", mb: 0.5, wordBreak: "break-word" }}>
                                        {msg.message}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          opacity: 0.7,
                                          display: "block",
                                          textAlign: "right",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {new Date(msg.timestamp).toLocaleString()}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                                  No messages in this conversation
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        </Collapse>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <MessageCircle size={48} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      No chat transcripts available yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Chat transcripts will appear here when users interact with your chatbot
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop
      open={loading}
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
    </Box>
  );
};

export default Dashboard;
